import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { apiClient } from '@/api/client';
import { Loader2, Shield, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { LEGAL_LINKS } from '@/lib/legalLinks';

/**
 * Login — Pam Golding green sign-in shell (shared @pgp/ui look; tokens vendored
 * in src/styles/pgp-ui.css). Alchemy SSO is primary; local email/password (with
 * optional 2FA) is a break-glass option behind a quiet toggle.
 */
export default function Login() {
  const navigate = useNavigate();
  const { login, loginViaAlchemy } = useAuth();
  const { appTheme } = useAppTheme();
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [params] = useSearchParams();
  // ?partner=1 (from the set-password page) opens the email form directly.
  const [showLocalForm, setShowLocalForm] = useState(params.get('partner') === '1');
  const [requires2FA, setRequires2FA] = useState(false);
  const [stubMode, setStubMode] = useState(false);
  const [form, setForm] = useState({ email: params.get('email') || '', password: '', twoFactorCode: '' });

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/auth/login-info')
      .then((resp) => { if (!cancelled && resp?.data?.stubMode) setStubMode(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleAlchemyLogin = async () => {
    setSsoLoading(true);
    const ok = await loginViaAlchemy();
    if (!ok) {
      setSsoLoading(false);
      toast.error('Could not start single sign-on. Try again or sign in as administrator.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error('Please fill in all fields'); return; }
    if (requires2FA && !form.twoFactorCode) { toast.error('Please enter your 2FA code'); return; }

    setLoading(true);
    try {
      const result = await login(form.email, form.password, form.twoFactorCode || undefined);
      if (result?.requires_2fa || result?.data?.requires_2fa) {
        setRequires2FA(true);
        toast.info('Please enter your two-factor authentication code');
        setLoading(false);
        return;
      }
      toast.success('Welcome back!');
      const redirectPath = localStorage.getItem('redirectAfterLogin');
      if (redirectPath) { localStorage.removeItem('redirectAfterLogin'); navigate(redirectPath); }
      else { navigate('/dashboard'); }
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.detail;
      const code = detail?.code || error?.code;
      const fallbackMessage = detail?.message || error?.message || 'Invalid credentials';
      if (code === 'USE_SSO') {
        toast.error('This account uses single sign-on. Use "Sign in with Alchemy" above.');
      } else {
        toast.error(fallbackMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const label = { display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.82)', margin: '14px 0 4px' };
  const darkInput = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,.2)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 14, fontFamily: 'inherit' };
  const linkBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'rgba(255,255,255,.72)', fontSize: 13, textDecoration: 'underline', cursor: 'pointer', marginTop: 16, fontFamily: 'inherit' };
  const hint = { fontSize: 11, color: 'rgba(255,255,255,.6)', margin: '4px 0 0', textAlign: 'left' };

  return (
    <div className="pgp-auth-shell pgp-scope" data-testid="login-page">
      <div className="pgp-auth-shell__inner">
        <img className="pgp-auth-shell__logo" src="/pgp-gates-white.svg" alt="Pam Golding Properties" />
        <p className="pgp-eyebrow">Pam Golding Properties</p>
        <h1 className="pgp-wordmark">LinkHub</h1>
        <div className="pgp-auth-shell__rule" />
        <p className="pgp-auth-shell__lede">
          {appTheme.login_subtitle || 'Sign in with your Pam Golding (Alchemy) account to continue.'}
        </p>

        {stubMode && (
          <p style={{ margin: '0 0 14px', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: '#f0c674' }} data-testid="login-stub-mode-badge">
            Stub mode — no real CRM is contacted
          </p>
        )}

        <button
          type="button"
          className="pgp-btn pgp-btn--on-dark"
          disabled={ssoLoading}
          onClick={handleAlchemyLogin}
          data-testid="sso-login-button"
        >
          {ssoLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Sign in with Alchemy&nbsp;&nbsp;→
        </button>
        <p style={{ ...hint, textAlign: 'center' }}>You'll be redirected to the Pam Golding CRM to sign in.</p>

        {!showLocalForm ? (
          <div>
            <button type="button" style={linkBtn} onClick={() => setShowLocalForm(true)} data-testid="show-admin-login-button">
              <KeyRound className="h-3.5 w-3.5" /> Partner or administrator sign-in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ marginTop: 18 }}>
            <p style={{ ...hint, marginBottom: 8 }} data-testid="admin-login-disclosure">
              For agency partners and administrators without an Alchemy account. Pam Golding staff should use the button above.
            </p>
            <label style={label} htmlFor="email">Email</label>
            <input id="email" type="email" style={darkInput} placeholder="you@company.com"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={requires2FA} data-testid="login-email-input" />
            <label style={label} htmlFor="password">Password</label>
            <input id="password" type="password" style={darkInput} placeholder="Enter your password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              disabled={requires2FA} data-testid="login-password-input" />

            {requires2FA && (
              <>
                <label style={label} htmlFor="twoFactorCode"><Shield className="h-4 w-4" /> Two-Factor Code</label>
                <input id="twoFactorCode" type="text" maxLength={6} autoFocus
                  style={{ ...darkInput, textAlign: 'center', letterSpacing: '.4em', fontFamily: 'Source Code Pro, monospace' }}
                  placeholder="------"
                  value={form.twoFactorCode}
                  onChange={(e) => setForm({ ...form, twoFactorCode: e.target.value.replace(/\D/g, '') })}
                  data-testid="login-2fa-input" />
                <p style={hint}>Enter the code from your authenticator app</p>
              </>
            )}

            <button type="submit" className="pgp-btn pgp-btn--on-dark" style={{ marginTop: 18 }} disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {requires2FA ? 'Verify & Sign in' : 'Sign in'}
            </button>

            {requires2FA && (
              <div>
                <button type="button" style={linkBtn} onClick={() => { setRequires2FA(false); setForm({ ...form, twoFactorCode: '' }); }}>
                  ← Back to login
                </button>
              </div>
            )}
            <div>
              <button type="button" style={linkBtn}
                onClick={() => { setShowLocalForm(false); setRequires2FA(false); setForm({ email: '', password: '', twoFactorCode: '' }); }}
                data-testid="hide-admin-login-button">
                Cancel
              </button>
            </div>
          </form>
        )}

        <p className="pgp-auth-shell__foot">
          By signing in, you agree to our{' '}
          <a href={LEGAL_LINKS.terms.href} target="_blank" rel="noopener noreferrer">Terms &amp; conditions</a>
          {' '}and{' '}
          <a href={LEGAL_LINKS.privacy.href} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
